import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CdkListboxModule } from '@angular/cdk/listbox';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AnnounceDirective } from '../../../directives/announce.directive';
import { getUniqueId } from '../../../functions/get-unique-id';
import { BaseComponent } from '../../base/base.component';
import { IconButtonComponent } from '../buttons/icon-button.component';
import { CountLabelComponent } from '../labels/count-label.component';
import { SearchFilterStatusComponent } from '../labels/search-filter-status.component';
import { HelpTextComponent } from './help-text.component';
import { InputComponent } from './input.component';

@Component({
  selector: 'app-pick-list',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    CdkListboxModule,
    CdkDropList,
    CdkDrag,
    IconButtonComponent,
    InputComponent,
    HelpTextComponent,
    CountLabelComponent,
    CdkDragPlaceholder,
    AnnounceDirective,
    SearchFilterStatusComponent,
    ScrollingModule,
  ],
  template: `
    <div
      class="tw-flex tw-w-full pick-list-layout"
      [ngClass]="classes"
      [appAnnounce]="statusMessage"
      [class.tw-pointer-events-none]="disabled"
      [class.picklist-disabled]="disabled"
      [style.width.px]="width"
    >
      <app-search-filter-status
        announceMode="filter"
        [count]="filteredCount"
        [query]="filter.value"
        statusStyle="matching"
        recordLabel="item"
        recordLabelPlural="items"
      />
      <app-search-filter-status
        announceMode="filter"
        [count]="filteredTargetCount"
        [query]="targetFilter.value"
        statusStyle="matching"
        recordLabel="item"
        recordLabelPlural="items"
      />
      <div class="pick-list-layout-inner">
        <!-- source list -->
        <div class="tw-basis-1/2">
          <div
            class="tw-font-bold tw-text-[12px] tw-text-[var(--sed-black-80-color)] tw-pb-1 tw-pl-1"
            [id]="availableCaptionId"
            aria-hidden="true"
          >
            {{ availableLabel }}: {{ filteredCount }} items
          </div>
          <div
            class="tw-rounded-lg tw-border tw-border-[var(--sed-grey-60-color)] tw-p-1"
          >
            @if (showFilter) {
              <div class="tw-py-2">
                <app-input
                  #filterInput
                  label="Filter available items"
                  [control]="filter"
                  placeholder="Filter"
                  [showHelpText]="false"
                  inputMode="search"
                  [showAudio]="true"
                />
              </div>
            }
            <div class="listbox-container tw-w-full ">
              <ul
                cdkListbox
                cdkDropList
                [cdkDropListData]="filteredNodes"
                [cdkListboxMultiple]="false"
                [cdkDropListConnectedTo]="[targetList]"
                (cdkDropListDropped)="sourceDrop($event)"
                [formControl]="selectedSource"
                [attr.aria-labelledby]="availableCaptionId"
                [class]="
                  inDialog ? 'listbox tw-h-[258px]' : 'listbox tw-h-[441px]'
                "
                #sourceList="cdkDropList"
                [tabindex]="0"
              >
                <cdk-virtual-scroll-viewport itemSize="34">
                  <li
                    *cdkVirtualFor="let node of filteredNodes"
                    class="tw-text-nowrap"
                  >
                    @if (node.type === 'group') {
                      @if (node.group.label !== '_DEFAULT') {
                        <div
                          class="tw-py-2 tw-px-2 tw-gap-2 tw-flex tw-items-center header tw-h-[38px]"
                        >
                          <div class="tw-font-bold">{{ node.group.label }}</div>
                          <app-count-label [label]="node.group.items.length" />
                        </div>
                      }
                    } @else {
                      <div
                        class="option tw-h-[34px]"
                        cdkDrag
                        [cdkOption]="node"
                        (dblclick)="addItems()"
                        (mousedown)="sourceMousedown(node)"
                      >
                        <div
                          class="tw-bg-[var(--sed-grey-60-color)] tw-rounded-lg  tw-h-[38px] tw-border-[var(--sed-black-80-color)] tw-border-2 tw-border-dotted tw-animate-pulse"
                          *cdkDragPlaceholder
                        ></div>

                        <span>
                          {{ node.item[labelName] }}
                        </span>
                      </div>
                    }
                  </li>
                </cdk-virtual-scroll-viewport>
              </ul>
            </div>
          </div>
        </div>

        <!-- add/remove buttons (aligned with dual-list-picker .transfer) -->
        <ul class="pick-list-transfer-buttons">
          @if (showAddRemoveAll) {
            <li class="tw-contents">
              <app-icon-button
                icon="fa-light fa-chevrons-right"
                mobileIcon="fa-light fa-chevrons-down"
                tooltip="Add all to selected"
                (click)="addAllItems()"
                [disabled]="disabled || !filteredCount"
              />
            </li>
          }

          <li class="tw-contents">
            <app-icon-button
              icon="fa-light fa-chevron-right"
              mobileIcon="fa-light fa-chevron-down"
              tooltip="Add selected to target"
              (click)="addItems()"
              [disabled]="disabled || !filteredCount"
            />
          </li>
          <li class="tw-contents">
            <app-icon-button
              icon="fa-light fa-chevron-left"
              mobileIcon="fa-light fa-chevron-up"
              tooltip="Remove selected from target"
              (click)="removeItems()"
              [disabled]="disabled || !filteredTargetCount"
            />
          </li>

          @if (showAddRemoveAll) {
            <li class="tw-contents">
              <app-icon-button
                icon="fa-light fa-chevrons-left"
                mobileIcon="fa-light fa-chevrons-up"
                tooltip="Remove all from selected"
                (click)="removeAllItems()"
                [disabled]="disabled || !filteredTargetCount"
              />
            </li>
          }
        </ul>

        <!-- target list -->
        <div class="tw-h-full tw-basis-1/2">
          <div
            class="tw-font-bold tw-text-[12px] tw-text-[var(--sed-black-80-color)] tw-pb-1 tw-pl-1"
            [id]="selectedCaptionId"
            aria-hidden="true"
          >
            {{ selectedLabel }}: {{ filteredTargetCount }} items
          </div>
          <div
            class="tw-rounded-lg tw-border tw-border-[var(--sed-grey-60-color)] tw-p-1 tw-h-full"
          >
            @if (showTargetFilter) {
              <div class="tw-py-2">
                <app-input
                  label="Filter selected items"
                  [control]="targetFilter"
                  placeholder="Filter"
                  [showHelpText]="false"
                  inputMode="search"
                  [showAudio]="true"
                />
              </div>
            }

            <div class="listbox-container tw-w-full">
              <ul
                cdkListbox
                [cdkListboxMultiple]="false"
                #targetList="cdkDropList"
                [id]="selectedListboxId"
                cdkDropList
                [cdkDropListData]="filteredTarget"
                [cdkDropListConnectedTo]="[sourceList]"
                (cdkDropListDropped)="targetDrop($event)"
                [formControl]="selectedTarget"
                [attr.aria-labelledby]="selectedCaptionId"
                [class]="
                  showTargetFilter
                    ? (inDialog ? 'listbox tw-h-[258px]' : 'listbox tw-h-[441px]')
                    : (inDialog ? 'listbox tw-h-[310px]' : 'listbox tw-h-[493px]')
                "
                (keyup)="targetKeyUp($event)"
                [tabindex]="0"
              >
                <cdk-virtual-scroll-viewport itemSize="38">
                  <li
                    *cdkVirtualFor="let item of filteredTarget"
                    class="tw-text-nowrap"
                  >
                    <div
                      [cdkOption]="item"
                      [id]="
                        'pick-target-' + selectedCaptionId + '-' + item[keyName]
                      "
                      class="option"
                      cdkDrag
                      (dblclick)="removeItems()"
                      (mousedown)="selectedTarget.setValue([item])"
                    >
                      <div
                        class="tw-bg-[var(--sed-grey-60-color)] tw-rounded-lg tw-h-[38px] tw-border-[var(--sed-black-80-color)] tw-border-2 tw-border-dotted tw-animate-pulse"
                        *cdkDragPlaceholder
                      ></div>
                      <span>
                        {{ item[labelName] }}
                      </span>
                    </div>
                  </li>
                </cdk-virtual-scroll-viewport>
              </ul>
            </div>
          </div>
          <app-help-text [control]="targetControl" [helpText]="helpText" />
        </div>

        <!-- move buttons -->
        @if (showMove) {
          <ul class="pick-list-reorder-buttons">
            <li class="tw-contents">
              <app-icon-button
                icon="fa-light fa-chevrons-up"
                tooltip="Move to top"
                (click)="moveTop()"
                [disabled]="disabled"
              />
            </li>
            <li class="tw-contents">
              <app-icon-button
                icon="fa-light fa-chevron-up"
                tooltip="Move up"
                (click)="moveUp()"
                [disabled]="disabled"
              />
            </li>
            <li class="tw-contents">
              <app-icon-button
                icon="fa-light fa-chevron-down"
                tooltip="Move down"
                (click)="moveDown()"
                [disabled]="disabled"
              />
            </li>
            <li class="tw-contents">
              <app-icon-button
                icon="fa-light fa-chevrons-down"
                tooltip="Move to bottom"
                (click)="moveBottom()"
                [disabled]="disabled"
              />
            </li>
          </ul>
        }
      </div>
    </div>

    @if (showSort) {
      <div class="tw-flex tw-items-center tw-gap-2 tw-text-base">
        <span class="tw-text-[var(--sed-black-80-color)]">Sort by:</span>
        <div
          class="tw-inline-flex tw-rounded-md tw-border tw-border-[var(--sed-grey-60-color)] tw-overflow-hidden"
        >
          <button
            type="button"
            class="tw-px-3 tw-py-1 tw-text-base tw-transition-colors sort-toggle-btn"
            [class.sort-toggle-active]="sortBy === 'code'"
            (click)="setSortBy('code')"
          >
            Code
          </button>
          <button
            type="button"
            class="tw-px-3 tw-py-1 tw-text-base tw-transition-colors sort-toggle-btn"
            [class.sort-toggle-active]="sortBy === 'description'"
            (click)="setSortBy('description')"
          >
            Description
          </button>
        </div>
      </div>
    }
  `,
  styles: `
    /* Scoped to app-pick-list only — desktop: normal flow, fits parent */
    .pick-list-layout {
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      height: auto;
      min-height: 0;
      min-width: 0;
      overflow: visible;
      max-width: 100%;
      width: 100%;
      box-sizing: border-box;
    }

    /* Desktop: single row — left | buttons | right (panels flex to fit, no wrap) */
    .pick-list-layout-inner {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: stretch;
      overflow-x: visible;
      max-width: 100%;
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    .pick-list-layout-inner > div {
      flex: 1 1 0;
      min-width: 0;
    }

    .pick-list-layout-inner > ul {
      flex-shrink: 0;
    }

    /* Match dual-list-picker .transfer / .reorder-buttons */
    .pick-list-transfer-buttons,
    .pick-list-reorder-buttons {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      align-self: center;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    @media (max-width: 639px) {
      .pick-list-layout {
        max-width: 100% !important;
        width: 100% !important;
      }

      .pick-list-layout-inner {
        flex-direction: column;
      }

      .pick-list-layout-inner > div {
        flex: 1 1 auto;
        width: 100%;
      }

      .pick-list-transfer-buttons,
      .pick-list-reorder-buttons {
        flex-direction: row;
        justify-content: center;
        padding: 0.5rem 0;
      }
    }

    /* Tablet (sm to lg) */
    @media (min-width: 640px) and (max-width: 1023px) {
      .pick-list-layout {
        max-width: 100% !important;
        width: 100% !important;
      }

      .pick-list-layout-inner {
        overflow-x: visible;
      }

      .pick-list-layout-inner > div {
        flex: 1 1 0;
        min-width: 0;
      }
    }

    cdk-virtual-scroll-viewport {
      height: 100%;
    }

    li {
      margin-bottom: 0px;
    }

    .listbox-container {
      -width: 280px;
      -overflow-y: scroll;
    }

    .listbox {
      padding: 0;
      margin: 0;
    }

    .header {
      border-bottom: 1px solid var(--sed-grey-40-color);
      margin-right: 4px;
    }

    .option {
      position: relative;
      padding: 2px 4px 2px 8px;
      margin-right: 4px;
      cursor: pointer;
      list-style: none;
      border-bottom: 1px solid var(--sed-grey-40-color);
      background: var(--sed-warm-white-color);
    }

    .option:hover,
    .cdk-option-active {
      background: var(--sed-black-60-color);
      color: var(--sed-warm-white-color);
    }

    .picklist-disabled .option {
      background: rgb(226, 232, 240);
      cursor: not-allowed;
    }

    .picklist-disabled .option:hover {
      background: rgb(226, 232, 240);
    }

    .picklist-disabled .listbox-container {
      background: rgb(226, 232, 240);
    }

    .picklist-disabled .tw-rounded-lg {
      background: rgb(226, 232, 240);
    }

    .sort-toggle-btn {
      background: transparent;
      color: var(--sed-black-80-color);
      border: none;
      cursor: pointer;
    }

    .sort-toggle-btn:hover {
      background: var(--sed-grey-60-color);
    }

    .sort-toggle-btn.sort-toggle-active {
      background: var(--sed-black-80-color);
      color: white;
    }
  `,
})
export class PickListComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  /**
   * Form control that stores array of all available items or
   * an array of PickListGroups. Picklist will filter out selected items.
   */
  @Input({ required: true })
  sourceControl: FormControl<any[] | PickListGroup[]>;

  /**
   * Form control provides array of selected items
   */
  @Input({ required: true })
  targetControl: FormControl<any[]>;

  /**
   * Field name on object to use for display label
   */
  @Input({ required: true })
  labelName: string;

  /**
   * Field name on object to use for object comparision
   */
  @Input({ required: true })
  keyName: string;

  /**
   * Helptext displayed below selected list
   */
  @Input()
  helpText: string;

  /**
   * Shows filter input on the available list
   */
  @Input()
  showFilter = true;

  /**
   * Shows filter input on the selected list
   */
  @Input()
  showTargetFilter = false;

  /**
   * Shows move buttons on the selected list
   */
  @Input()
  showMove = true;

  /**
   * Shows "Add All" and "Remove All" buttons (double chevrons)
   */
  @Input()
  showAddRemoveAll = false;

  /**
   * Shows sort toggle (Code/Description) below the lists
   */
  @Input()
  showSort = false;

  /**
   * Property name used for sorting by "code" (defaults to keyName)
   */
  @Input()
  sortCodeKey: string = null;

  /**
   * Property name used for sorting by "description" (defaults to labelName)
   */
  @Input()
  sortDescKey: string = null;

  @Input()
  inDialog = false;

  /**
   * Disables all interactions with the pick list
   */
  @Input()
  disabled = false;

  @Output()
  beforeRemoveItems = new EventEmitter<RemoveItemsEvent>();

  @Output()
  targetChanged = new EventEmitter<any[]>();

  @Input()
  autoFocusFilter = false;

  /**
   * When true, preserves the original order of items instead of sorting alphabetically
   */
  @Input()
  preserveOrder = false;

  /**
   * Width of component in pixels
   */
  @Input()
  width = 700;

  @ViewChild('filterInput')
  filterInput: InputComponent;

  availableCaptionId = getUniqueId('picklist-available');
  selectedCaptionId = getUniqueId('picklist-selected');
  selectedListboxId = getUniqueId('picklist-selected-listbox');

  protected target: any[];

  protected availableLabel = 'Available';

  protected selectedLabel = 'Selected';

  protected filter = new FormControl<string>(null);

  protected targetFilter = new FormControl<string>(null);

  protected sortBy: 'code' | 'description' = 'code';

  public selectedSource = new FormControl<PickListNode[]>([]);

  protected selectedTarget = new FormControl<any[]>([]);

  protected filteredTarget: any[];

  private filteredGroups: PickListGroup[];

  protected filteredNodes: PickListNode[];

  protected filteredCount = 0;

  protected filteredTargetCount = 0;

  protected statusMessage = '';

  protected classes: string;

  ngOnInit(): void {
    this.updateSource();
    this.dispose.subs = this.filter.valueChanges.subscribe(() =>
      this.updateSource(),
    );

    this.dispose.subs = this.targetFilter.valueChanges.subscribe(() =>
      this.updateTarget(),
    );

    this.dispose.subs = this.sourceControl.valueChanges.subscribe(() =>
      this.updateSource(),
    );

    this.dispose.subs = this.targetControl.valueChanges.subscribe((x) => {
      this.updateSource();
    });
  }

  ngAfterViewInit(): void {
    if (this.showFilter && this.autoFocusFilter) {
      queueMicrotask(() => this.filterInput?.focus());
    }
  }

  targetDrop(event: CdkDragDrop<any>) {
    if (event.previousContainer === event.container) {
      // move the items in the underlying array then recompute filtered array
      const droppedKey = this.selectedTarget.value[0][this.keyName];
      const previousIndex = this.target.findIndex(
        (x) => x[this.keyName] === droppedKey,
      );
      moveItemInArray(this.target, previousIndex, event.currentIndex);
      this.updateTarget();
    } else {
      const droppedItem = this.selectedSource.value[0].item;
      this.selectedSource.setValue([]);

      // insert the source item into the location on target
      const currentIndex = event.currentIndex;
      if (currentIndex === -1) {
        this.target.push(droppedItem);
      } else {
        this.target.splice(currentIndex, 0, droppedItem);
      }
      this.updateSource();
    }

    this.targetChanged.emit(this.target);
  }

  /**
   * Dropping on source removes item from target
   * and displays in source. Source cannot be
   * reordered
   */
  sourceDrop(event: CdkDragDrop<any>) {
    if (event.previousContainer !== event.container) {
      this.removeItems();
    }
  }

  sourceMousedown(node: PickListNode) {
    this.selectedSource.setValue([node]);
  }

  updateSource() {
    // if we do not have a provided grouping, create one
    this.filteredGroups = [];
    this.target ??= this.targetControl.value;
    if (this.sourceControl.value[0] instanceof PickListGroup) {
      for (const group of this.sourceControl.value as PickListGroup[]) {
        this.filteredGroups.push(
          new PickListGroup(group.label, [...group.items]),
        );
      }
    } else {
      this.filteredGroups.push(
        new PickListGroup('_DEFAULT', [...this.sourceControl.value]),
      );
    }

    // create a shallow copy of the items array so the original source is not modified
    for (const group of this.filteredGroups) {
      group.items = [...group.items];
    }

    const filter = this.filter.value;
    if (filter) {
      this.availableLabel = 'Filtered';
      for (const group of this.filteredGroups) {
        group.items = group.items.filter(
          (x) =>
            (x[this.labelName] as string)
              .toLowerCase()
              .indexOf(filter.toLowerCase()) > -1,
        );
      }
    } else {
      this.availableLabel = 'Available';
    }

    // remove selected items from source
    this.target = this.targetControl.value;
    for (const item of this.target) {
      for (const group of this.filteredGroups) {
        const found = group.items.find(
          (x) => x[this.keyName] === item[this.keyName],
        );
        if (found) {
          group.items.splice(group.items.indexOf(found), 1);
        }
      }
    }

    // count the items
    this.filteredCount = 0;
    for (const group of this.filteredGroups) {
      this.filteredCount += group.items.length;
    }

    // sort each group (unless preserveOrder is true)
    if (!this.preserveOrder) {
      const sortKey = this.showSort
        ? this.sortBy === 'code'
          ? this.sortCodeKey || this.keyName
          : this.sortDescKey || this.labelName
        : this.labelName;

      const sortByDescription = this.showSort && this.sortBy === 'description';

      for (const group of this.filteredGroups) {
        group.items.sort((a, b) => {
          let aVal = String(a[sortKey] || '').toLowerCase();
          let bVal = String(b[sortKey] || '').toLowerCase();

          // When sorting by description, extract the part after " - " if present
          if (sortByDescription) {
            const aSplit = aVal.split(' - ');
            const bSplit = bVal.split(' - ');
            if (aSplit.length > 1) aVal = aSplit.slice(1).join(' - ');
            if (bSplit.length > 1) bVal = bSplit.slice(1).join(' - ');
          }

          return aVal.localeCompare(bVal);
        });
      }
    }

    // flatten nodes for virtual support
    this.filteredNodes = [];
    for (const group of this.filteredGroups) {
      this.filteredNodes.push({
        type: 'group',
        group,
        item: null,
        key: 'group-' + group.label,
      });
      for (const item of group.items) {
        this.filteredNodes.push({
          type: 'item',
          group: null,
          item,
          key: 'item-' + item[this.keyName],
        });
      }
    }

    this.targetControl?.setValue(this.target, { emitEvent: false });
    this.targetControl?.markAsTouched();

    this.updateTarget();
  }

  updateTarget() {
    const targetFilterValue = this.targetFilter.value;
    if (targetFilterValue) {
      this.selectedLabel = 'Filtered';
      this.filteredTarget = this.target.filter(
        (x) =>
          (x[this.labelName] as string)
            .toLowerCase()
            .indexOf(targetFilterValue.toLowerCase()) > -1,
      );
    } else {
      this.selectedLabel = 'Selected';
      this.filteredTarget = [...this.target];
    }

    // When showMove is on, target order is user-controlled (reorder / drag); do not
    // re-sort the selected list or every move would appear to do nothing.
    // Apply sorting to target only when sort UI is on and reorder is off.
    if (this.showSort && !this.preserveOrder && !this.showMove) {
      const sortKey =
        this.sortBy === 'code'
          ? this.sortCodeKey || this.keyName
          : this.sortDescKey || this.labelName;
      const sortByDescription = this.sortBy === 'description';

      this.filteredTarget.sort((a, b) => {
        let aVal = String(a[sortKey] || '').toLowerCase();
        let bVal = String(b[sortKey] || '').toLowerCase();

        // When sorting by description, extract the part after " - " if present
        if (sortByDescription) {
          const aSplit = aVal.split(' - ');
          const bSplit = bVal.split(' - ');
          if (aSplit.length > 1) aVal = aSplit.slice(1).join(' - ');
          if (bSplit.length > 1) bVal = bSplit.slice(1).join(' - ');
        }

        return aVal.localeCompare(bVal);
      });
    }

    this.filteredTargetCount = this.filteredTarget.length;
  }

  targetKeyUp(event: KeyboardEvent) {
    if (event.key === 'Delete') {
      this.removeItems();
    }
  }

  addItems() {
    const added: any[] = [];
    for (const node of this.selectedSource.value) {
      const alreadySelected = this.target.some(
        (t) => t[this.keyName] === node.item[this.keyName],
      );
      if (alreadySelected) {
        continue;
      }
      this.target.push(node.item);
      added.push(node.item);
      const removeIndex = this.filteredNodes.indexOf(node);
      if (removeIndex >= 0) {
        this.filteredNodes.splice(removeIndex, 1);
      }
    }

    if (added.length === 0) {
      return;
    }

    this.filteredNodes = [...this.filteredNodes];

    this.selectedTarget.setValue([]);
    this.updateSource();
    this.announceAction(added, 'added to selected');
    this.targetChanged.emit(this.target);
    setTimeout(() => {
      this.selectedSource.setValue([]);
    }, 1);
  }

  addAllItems() {
    const nodes = [...this.filteredNodes];
    if (nodes.length === 0) {
      return;
    }

    const added: any[] = [];
    for (const node of nodes) {
      if (node.type === 'item') {
        this.target.push(node.item);
        added.push(node.item);
      }
    }
    this.selectedSource.setValue([]);
    this.selectedTarget.setValue([]);
    this.updateSource();

    this.announceAction(added, 'added to selected');
    this.targetChanged.emit(this.target);
  }

  removeItem(item: any) {
    const index = this.target.indexOf(item);
    if (index >= 0) {
      this.target.splice(index, 1);
      this.updateSource();
      this.targetChanged.emit(this.target);
      this.announceAction([item], 'removed from selected');

      queueMicrotask(() => {
        const nextIndex = Math.min(index, this.filteredTarget.length - 1);
        if (nextIndex >= 0) {
          const nextKey = this.filteredTarget[nextIndex][this.keyName];
          document
            .getElementById(
              'pick-target-' + this.selectedCaptionId + '-' + nextKey,
            )
            ?.focus();
        } else {
          document.getElementById(this.selectedListboxId)?.focus();
        }
      });
    }
  }

  removeItems() {
    const event = new RemoveItemsEvent();
    event.items = [...(this.selectedTarget.value || [])];
    this.beforeRemoveItems.emit(event);
    if (event.cancel) {
      return;
    }

    const items = event.items;
    for (const item of items) {
      this.target.splice(this.target.indexOf(item), 1);
    }

    this.selectedSource.setValue([]);
    this.selectedTarget.setValue([]);
    this.updateSource();

    this.announceAction(items, 'removed from selected');
    this.targetChanged.emit(this.target);
  }

  removeAllItems() {
    const items = [...this.target];
    if (items.length === 0) return;

    this.target.splice(0, this.target.length);
    this.selectedSource.setValue([]);
    this.selectedTarget.setValue([]);
    this.updateSource();

    this.announceAction(items, 'removed from selected');
    this.targetChanged.emit(this.target);
  }

  setSortBy(sort: 'code' | 'description') {
    this.sortBy = sort;
    this.updateSource();
  }

  moveTop() {
    const items = this.selectedTarget.value;
    if (items.length === 0) return;

    const oldPosition = this.target.indexOf(items[0]);
    for (const item of items.reverse()) {
      moveItemInArray(this.target, this.target.indexOf(item), 0);
    }

    const newPosition = this.target.indexOf(items[0]);
    if (newPosition !== oldPosition) {
      this.announceAction(
        items,
        `moved to position 1 of ${this.target.length}`,
      );
    }

    this.targetControl?.setValue(this.target, { emitEvent: false });
    this.updateTarget();
    this.targetChanged.emit(this.target);
  }

  moveUp() {
    const items = this.selectedTarget.value;
    if (items.length === 0) return;

    const oldPosition = this.target.indexOf(items[0]);
    for (const item of items) {
      const index = this.target.indexOf(item);
      if (index > 0) {
        moveItemInArray(this.target, index, index - 1);
      }
    }

    const newPosition = this.target.indexOf(items[0]);
    if (newPosition !== oldPosition) {
      this.announceAction(
        items,
        `moved up to position ${newPosition + 1} of ${this.target.length}`,
      );
    }

    this.targetControl?.setValue(this.target, { emitEvent: false });
    this.updateTarget();
    this.targetChanged.emit(this.target);
  }

  moveDown() {
    const items = this.selectedTarget.value;
    if (items.length === 0) return;

    const oldPosition = this.target.indexOf(items[0]);
    for (const item of items.reverse()) {
      const index = this.target.indexOf(item);
      if (index < this.target.length - 1) {
        moveItemInArray(this.target, index, index + 1);
      }
    }

    const newPosition = this.target.indexOf(items[0]);
    if (newPosition !== oldPosition) {
      this.announceAction(
        items,
        `moved down to position ${newPosition + 1} of ${this.target.length}`,
      );
    }

    this.targetControl?.setValue(this.target, { emitEvent: false });
    this.updateTarget();
    this.targetChanged.emit(this.target);
  }

  moveBottom() {
    const items = this.selectedTarget.value;
    if (items.length === 0) return;

    const oldPosition = this.target.indexOf(items[0]);
    for (const item of items) {
      moveItemInArray(
        this.target,
        this.target.indexOf(item),
        this.target.length - 1,
      );
    }

    const newPosition = this.target.indexOf(items[0]);
    if (newPosition !== oldPosition) {
      this.announceAction(
        items,
        `moved to position ${this.target.length} of ${this.target.length}`,
      );
    }

    this.targetControl?.setValue(this.target, { emitEvent: false });
    this.updateTarget();
    this.targetChanged.emit(this.target);
  }

  /** Announces action to screen readers. Only call when message will be unique. */
  private announceAction(items: any[], action: string): void {
    if (items.length === 0) return;
    this.statusMessage = `${this.formatItemsText(items)} ${action}`;
  }

  private formatItemsText(items: any[]): string {
    if (items.length === 1) {
      return items[0][this.labelName];
    }
    return `${items.length} items`;
  }
}

export class RemoveItemsEvent {
  cancel = false;
  items: any[];
}

export class PickListGroup {
  constructor(
    public label: string,
    public items: any[],
  ) {}
}

type PickListNodeType = 'item' | 'group';

class PickListNode {
  constructor(
    public type: PickListNodeType,

    public group: PickListGroup,

    public item: any,

    public key: string,
  ) {}
}
